<template>
  <section id="hotels">
    <h3>Hotels</h3>
    <Route v-if="!isSavedRoute" v-bind="$attrs" v-model:hotels="hotels" v-model:is-saved-route="isSavedRoute" v-model:leg-index="legIndex" @error="$emit('error', $event)" @scroll="$emit('scroll', $event)" @back="$emit('back', $event)" />
    <Options v-if="isSavedRoute && !isSavedHotels && isSelfBook" v-bind="$attrs" v-model:hotels="hotels" v-model:is-saved-hotels="isSavedHotels" v-model:is-saved-route="isSavedRoute" v-model:leg-index="legIndex" :organization-id="organizationId" @error="$emit('error', $event)" @scroll="$emit('scroll', $event)" @update:is-self-book="$emit('update:isSelfBook', $event)" />
    <SummaryAssist v-if="isSavedRoute && !isSelfBook" v-bind="$attrs" v-model:hotels-other="hotels.other" v-model:is-saved-route="isSavedRoute" :hotels="hotels" @complete="complete" @scroll="$emit('scroll', $event)" />
    <SummaryBook v-if="isSavedRoute && isSavedHotels && isSelfBook" v-bind="$attrs" v-model:hotels="hotels" v-model:is-saved-hotels="isSavedHotels" v-model:is-saved-route="isSavedRoute" v-model:leg-index="legIndex" @complete="complete" @scroll="$emit('scroll', $event)" />
  </section>
</template>

<script setup>
import { computed, ref } from "vue";
import DateTime from "../utils/datetime";
import Options from "./TripNewBookHotelsOptions.vue";
import Route from "./TripNewBookHotelsRoute.vue";
import SummaryAssist from "./TripNewBookHotelsSummaryAssist.vue";
import SummaryBook from "./TripNewBookHotelsSummaryBook.vue";

const emit = defineEmits(["error", "update:hotels", "update:sectionsHotelsDateStart", "update:sectionsHotelsDateEnd", "update:sectionsHotelsDestination", "update:sectionsHotelsHotels", "update:sectionsHotelsDescription", "update:sectionsHotelsIsComplete", "update:isSelfBook", "scroll", "back"]);
const props = defineProps(["isSelfBook", "organizationId"]);
const hotels = ref({ other: null, route: [{ key: 1, checkin: "", checkout: "", ideal: [], place: "", rooms: [{ adults: 1 }] }] });
const isSavedHotels = ref(false);
const isSavedRoute = ref(false);
const legIndex = ref(0);

const { utilDateISO8601, utilDateFlightfoxShort } = DateTime();

// Computed properties
const hotelsDateStart = computed(() => utilDateISO8601(hotels.value.route[0].checkin));
const hotelsDateEnd = computed(() => utilDateISO8601(hotels.value.route[hotels.value.route.length - 1].checkout));

const hotelsDescription = computed(() => {
  let desc = "\r\n\r\n***\r\n\r\nHOTELS\r\n\r\nSchedule:\r\n";
  desc += hotelsRouteToText.value;

  if (props.isSelfBook) {
    desc += "\r\n\r\nIdeal:\r\n";
    desc += hotelsIdealToText.value;
  } else {
    desc += "\r\n\r\n" + hotels.value.other;
  }

  return desc;
});

const hotelsDestination = computed(() => {
  const destArray = hotels.value.route.map(leg => leg.place.name);
  if (destArray.length === 1) return destArray[0];
  if (destArray.length > 3) return "Multiple Cities";

  const destLast = destArray.pop();
  return `${destArray.join(", ")} & ${destLast}`;
});

const hotelsHotels = computed(() => hotels.value.route.filter(leg => leg.ideal.name).map(leg => leg.ideal));

const hotelsIdealToText = computed(() => {
  return hotels.value.route
    .filter(leg => leg.checkin)
    .map(leg => {
      let output = "";
      if (leg.ideal.price_total) {
        const price = `${leg.ideal.price_currency} ${parseFloat(leg.ideal.price_total).toFixed(2).toLocaleString("en-US", {
          minimumFractionDigits: 2,
          useGrouping: true,
        })}`;
        output += `${price}\n`;
      }
      output += leg.ideal.is_refundable ? "Refundable\n" : "Non-refundable\n";
      output += `${leg.ideal.name}\n`;
      leg.rooms.forEach(room => {
        output += `${leg.ideal.room_name} (Adults: ${room.adults})\n`;
      });
      return output;
    })
    .join("")
    .trim();
});

const hotelsRouteToText = computed(() => {
  return hotels.value.route
    .map(data => {
      if (data.checkin) {
        const checkin = utilDateFlightfoxShort(data.checkin);
        const checkout = utilDateFlightfoxShort(data.checkout);
        return `${checkin} to ${checkout} - ${data.place.name}\n`;
      }
      return "";
    })
    .join("")
    .trim();
});

// Methods
const complete = () => {
  if (props.isSelfBook) {
    emit("update:sectionsHotelsDateStart", hotelsDateStart.value);
    emit("update:sectionsHotelsDateEnd", hotelsDateEnd.value);
    emit("update:sectionsHotelsDestination", hotelsDestination.value);
    emit("update:sectionsHotelsHotels", hotelsHotels.value);
  }

  emit("update:hotels", hotels.value);
  emit("update:sectionsHotelsDescription", hotelsDescription.value);
  emit("update:sectionsHotelsIsComplete", true);
  emit("scroll");
};
</script>
